import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query from React.
 *
 * Returns whether `query` currently matches, and re-renders when it flips
 * (viewport resize, device rotation, dev-tools device toolbar).
 *
 * When `window.matchMedia` is unavailable — jsdom does not implement it — the
 * hook reports `false`. That is deliberate and safe rather than a silent
 * fallback: every caller uses this to opt IN to a narrower layout, so `false`
 * means "render the wide layout", which is the behaviour that shipped before
 * any compact layout existed. A test that wants the compact branch must stub
 * `window.matchMedia` explicitly.
 */
export const useMediaQuery = (query) => {
  const supported =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';

  const [matches, setMatches] = useState(() =>
    supported ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (!supported) return undefined;

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    // Re-read on subscribe: `query` may have changed since the initial state
    // was computed, and the listener only fires on the NEXT flip.
    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query, supported]);

  return matches;
};

export default useMediaQuery;
